import {
  AudioLines,
  Box,
  Cable,
  Cpu,
  FileSpreadsheet,
  History,
  Image,
  LayoutTemplate,
  ListVideo,
  Server,
  Workflow,
} from "lucide-react";
import { useSingleStudio } from "@/hooks/useStudios";
import {
  useConfigVersions,
  useSingleStudioConfig,
} from "@/hooks/useStudioConfigs";
import { useStudioConnections } from "@/hooks/useStudioConnections";
import { Badge } from "@/components/ui/badge";
import { matchContextPath } from "../match";
import type { ContextParams, NavContextDef, NavItem } from "../types";

export const studioConfigPath = (studioId: string, configId: string) =>
  `/studios/${studioId}/config/${configId}`;

/**
 * Section counts (L6) from data that is cheap to read: the studio's devices
 * and the stored config data. A live config's editor reads layouts and
 * profiles from the bridge, which syncs back into the stored data.
 */
function useSectionBadges(studioId: string, configId: string) {
  const { data: connections, isLoading: devicesLoading } =
    useStudioConnections(studioId);
  const { data: config } = useSingleStudioConfig(configId);
  const { data: versions, isLoading: versionsLoading } =
    useConfigVersions(configId);
  const data = config?.config_data;
  return {
    devices: devicesLoading ? null : connections.length,
    layouts: data ? (data.layouts?.length ?? 0) : null,
    dante: data ? (data.dante?.devices?.length ?? 0) : null,
    history: versionsLoading ? null : versions.length,
  };
}

function useStudioConfigItems({
  studioId,
  configId,
}: ContextParams): NavItem[] {
  const base = studioConfigPath(studioId, configId);
  const badges = useSectionBadges(studioId, configId);
  return [
    {
      key: "devices",
      label: "Devices",
      href: base,
      exact: true,
      icon: Cpu,
      badge: badges.devices,
      testId: "editor-tab-devices",
    },
    {
      key: "layouts",
      label: "Layouts",
      href: `${base}/layouts`,
      icon: LayoutTemplate,
      badge: badges.layouts,
      testId: "editor-tab-layouts",
    },
    {
      key: "wiring",
      label: "Wiring",
      href: `${base}/wiring`,
      icon: Cable,
      testId: "editor-tab-wiring",
    },
    {
      key: "gear",
      label: "Temporary gear",
      href: `${base}/gear`,
      icon: Box,
      testId: "config-editor-tab-gear",
    },
    {
      key: "lmio",
      label: "lm-io-server",
      href: `${base}/lmio`,
      icon: Server,
      testId: "config-editor-tab-lmio",
    },
    {
      key: "dante",
      label: "Dante",
      href: `${base}/dante`,
      icon: AudioLines,
      badge: badges.dante,
      testId: "editor-tab-dante",
    },
    {
      key: "playout",
      label: "Playout",
      href: `${base}/playout/playlists`,
      activePrefix: `${base}/playout`,
      icon: ListVideo,
      testId: "config-editor-tab-playout",
      children: [
        {
          key: "playout/playlists",
          label: "Playlists",
          href: `${base}/playout/playlists`,
          testId: "config-playout-tab-playlists",
        },
        {
          key: "playout/playouts",
          label: "Playouts",
          href: `${base}/playout/playouts`,
          testId: "config-playout-tab-playouts",
        },
        {
          key: "playout/artwork",
          label: "Artwork",
          href: `${base}/playout/artwork`,
          testId: "config-playout-tab-artwork",
        },
        {
          key: "playout/settings",
          label: "Settings",
          href: `${base}/playout/settings`,
          testId: "config-playout-tab-settings",
        },
      ],
    },
    {
      key: "media",
      label: "Media",
      href: `${base}/media`,
      icon: Image,
      testId: "config-editor-tab-media",
    },
    {
      key: "history",
      label: "History",
      href: `${base}/history`,
      icon: History,
      badge: badges.history,
      testId: "config-editor-tab-history",
    },
    {
      key: "patch-sheet",
      label: "Patch sheet",
      href: `${base}/patch-sheet`,
      icon: FileSpreadsheet,
    },
  ];
}

function useStudioConfigHeader({ studioId, configId }: ContextParams) {
  const { data: config } = useSingleStudioConfig(configId);
  const { data: studio } = useSingleStudio(studioId);
  if (!config) return {};
  const isActive = studio?.active_config_id === configId;
  return {
    kicker: config.kind === "template" ? "Template" : "Config",
    title: config.name,
    status: isActive ? <Badge variant="default">Active</Badge> : null,
  };
}

function useStudioName({ studioId }: ContextParams) {
  return useSingleStudio(studioId).data?.name;
}

export const studioConfigContext: NavContextDef = {
  type: "studio-config",
  typeLabel: "Config",
  icon: Workflow,
  match: (pathname) =>
    matchContextPath("/studios/:studioId/config/:configId", pathname),
  key: ({ configId }) => `studio-config:${configId}`,
  ownerKey: ({ studioId }) => `studio:${studioId}`,
  base: ({ studioId, configId }) => studioConfigPath(studioId, configId),
  parent: ({ studioId }) => ({
    label: "Studio",
    href: `/studios/${studioId}/configurations`,
  }),
  useParentLabel: useStudioName,
  useHeader: useStudioConfigHeader,
  useItems: useStudioConfigItems,
};
